import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import LocationPermissionPopup from '../components/LocationPermissionPopup';

const LocationPermissionContext = createContext(null);

export const PERMISSION_STATUS = {
    PROMPT: 'prompt',
    GRANTED: 'granted',
    DENIED: 'denied',
    UNAVAILABLE: 'unavailable'
};

export function LocationPermissionProvider({ children }) {
    const [permissionStatus, setPermissionStatus] = useState(PERMISSION_STATUS.PROMPT);
    const [location, setLocation] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [pendingCallback, setPendingCallback] = useState(null);

    // Check initial permission status
    useEffect(() => {
        checkPermissionStatus();
    }, []);

    const checkPermissionStatus = async () => {
        if (!navigator.geolocation) {
            setPermissionStatus(PERMISSION_STATUS.UNAVAILABLE);
            return;
        }

        // Check if permission API is available
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const result = await navigator.permissions.query({ name: 'geolocation' });
                setPermissionStatus(result.state);

                if (result.state === PERMISSION_STATUS.GRANTED) {
                    fetchLocation();
                }

                // Listen for permission changes
                result.addEventListener('change', () => {
                    setPermissionStatus(result.state);
                    if (result.state === PERMISSION_STATUS.GRANTED) {
                        // Auto-fetch location when permission is granted
                        fetchLocation();
                    }
                });
            } catch (error) {
                console.log('Permission API not supported, using localStorage fallback');
                // Fallback to localStorage
                const storedStatus = localStorage.getItem('locationPermissionStatus');
                if (storedStatus) {
                    setPermissionStatus(storedStatus);
                }
            }
        } else {
            // Fallback to localStorage for browsers without Permission API
            const storedStatus = localStorage.getItem('locationPermissionStatus');
            if (storedStatus) {
                setPermissionStatus(storedStatus);
            }
        }
    };

    const fetchLocation = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                reject(new Error('Geolocation not supported'));
                return;
            }

            setIsLoading(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const coords = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    };
                    setLocation(coords);
                    setPermissionStatus(PERMISSION_STATUS.GRANTED);
                    localStorage.setItem('locationPermissionStatus', PERMISSION_STATUS.GRANTED);
                    setIsLoading(false);
                    resolve(coords);
                },
                (error) => {
                    console.error('Geolocation error:', error);
                    setIsLoading(false);

                    if (error.code === error.PERMISSION_DENIED) {
                        setPermissionStatus(PERMISSION_STATUS.DENIED);
                        localStorage.setItem('locationPermissionStatus', PERMISSION_STATUS.DENIED);
                    }
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 300000 // 5 minutes cache
                }
            );
        });
    }, []);

    // Request location - shows popup if previously denied
    const requestLocation = useCallback((onSuccess, onError) => {
        if (permissionStatus === PERMISSION_STATUS.DENIED) {
            // Show popup to guide user
            setPendingCallback({ onSuccess, onError });
            setShowPopup(true);
            return;
        }

        if (permissionStatus === PERMISSION_STATUS.UNAVAILABLE) {
            if (onError) {
                onError(new Error('Geolocation not available'));
            }
            return;
        }

        // Try to get location
        fetchLocation()
            .then((coords) => {
                if (onSuccess) onSuccess(coords);
            })
            .catch((error) => {
                if (error.code === 1) { // PERMISSION_DENIED
                    // Show popup on first denial too
                    setPendingCallback({ onSuccess, onError });
                    setShowPopup(true);
                } else if (onError) {
                    onError(error);
                }
            });
    }, [permissionStatus, fetchLocation]);

    // Handle popup "Enable Location" click
    const handleEnableLocation = useCallback(() => {
        setShowPopup(false);

        // Reset permission status to allow re-prompt
        setPermissionStatus(PERMISSION_STATUS.PROMPT);
        localStorage.removeItem('locationPermissionStatus');

        // Try to fetch location again
        fetchLocation()
            .then((coords) => {
                if (pendingCallback?.onSuccess) {
                    pendingCallback.onSuccess(coords);
                }
                setPendingCallback(null);
            })
            .catch((error) => {
                if (pendingCallback?.onError) {
                    pendingCallback.onError(error);
                }
                setPendingCallback(null);
            });
    }, [fetchLocation, pendingCallback]);

    const handleClosePopup = useCallback(() => {
        setShowPopup(false);
        if (pendingCallback?.onError) {
            pendingCallback.onError(new Error('User cancelled location request'));
        }
        setPendingCallback(null);
    }, [pendingCallback]);

    // Manual trigger to show popup (for Profile screen)
    const showLocationSettings = useCallback(() => {
        setShowPopup(true);
    }, []);

    const value = {
        permissionStatus,
        location,
        isLoading,
        requestLocation,
        showLocationSettings,
        fetchLocation
    };

    return (
        <LocationPermissionContext.Provider value={value}>
            {children}
            {showPopup && (
                <LocationPermissionPopup
                    onEnable={handleEnableLocation}
                    onClose={handleClosePopup}
                    permissionStatus={permissionStatus}
                />
            )}
        </LocationPermissionContext.Provider>
    );
}

export function useLocationPermission() {
    const context = useContext(LocationPermissionContext);
    if (!context) {
        throw new Error('useLocationPermission must be used within a LocationPermissionProvider');
    }
    return context;
}

export default LocationPermissionContext;
